import type { Survey, SurveyOverallScores, SurveyScoresByCategory } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import {
    createFileRoute,
    type ErrorComponentProps,
    redirect,
    retainSearchParams,
    SearchParamError,
    stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { NotFound, NotFoundError } from '@/components/NotFound';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import {
    SurveyCategoriesComparisonChart,
    SurveyCategoriesComparisonChartSkeleton,
} from '@/components/surveys/categories/SurveyCategoriesComparisonChart';
import {
    SurveyCategoriesComparisonTable,
    SurveyCategoriesComparisonTableSkeleton,
} from '@/components/surveys/categories/SurveyCategoriesComparisonTable';
import {
    SurveyCategoryXAttributesComparisonChart,
    SurveyCategoryXAttributesComparisonChartSkeleton,
} from '@/components/surveys/categories/SurveyCategoryXAttributesComparisonChart';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';
import { SelectSurvey } from '@/components/surveys/common/SelectSurvey';
import { fetchSurveys } from '@/queries/surveys/client';
import { pageBreakBefore, pageBreakBeforeInLandscape } from '@/styles/print.css';
import { formatDateToLabel } from '@/utils';
import {
    useOverallScores,
    useScoresByCategory,
    useScoresForAttributeComparisonChart,
    useScoresForCategoriesComparisonTable,
    useSurveyAttributes,
} from '../-hooks';
import { resolveScoresChain } from '../-utils';

const DefaultRouteQueryParams = {
    surveyId: '',
};

const RouteQueryParams = z.object({
    surveyId: uuidSchema.default(DefaultRouteQueryParams.surveyId),
});

export const Route = createFileRoute('/_client/_auth/surveys/categories/')({
    component: SurveyCategoriesRoute,
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [retainSearchParams(['surveyId']), stripSearchParams(DefaultRouteQueryParams)],
    },
    loaderDeps: ({ search }) => ({ surveyId: search.surveyId }),
    loader: async ({ deps }) => {
        const { surveys } = await fetchSurveys();

        if (deps.surveyId === '' && surveys.length > 0) {
            // Canonicalizes the URL to the survey actually being shown, so a shared/reloaded
            // link keeps pointing at it rather than silently drifting to whatever's newest.
            throw redirect({ to: '/surveys/categories', search: { surveyId: surveys[0].surveyId }, replace: true });
        }

        if (!surveys.some((survey) => survey.surveyId === deps.surveyId)) {
            throw new NotFoundError();
        }

        return { surveys };
    },
    pendingComponent: SurveyCategoriesRouteSkeleton,
    errorComponent: SurveyCategoriesRouteError,
});

function SurveyCategoriesRoute() {
    // Surveys are sorted from newest to oldest
    const { surveys } = Route.useLoaderData();
    const { surveyId } = Route.useSearch();
    const navigate = Route.useNavigate();

    // The loader redirects an empty `surveyId` to the newest survey's id, and rejects a
    // non-matching one, so this is always a valid index once there's at least one survey.
    const selectedSurveyIdx = surveys.findIndex((s) => s.surveyId === surveyId);
    const surveysToLoad = surveys.slice(selectedSurveyIdx);

    const scoresByCategoryQuery = useScoresByCategory(surveysToLoad.map((s) => s.surveyId));
    const overallScoresQuery = useOverallScores(surveysToLoad.map((s) => s.surveyId));
    const selectedSurveyOverallScores = overallScoresQuery.data?.scores.find((s) => s.surveyId === surveyId);

    if (surveys.length === 0) {
        // TODO: Visual design for empty state
        return <div>サーベイがありません。</div>;
    }

    const selectedSurvey = surveys[selectedSurveyIdx];

    return (
        <Page.Root>
            <Page.Header title="結果分析 - カテゴリー" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    <SelectSurvey
                        surveys={surveys.map((survey) => ({
                            id: survey.surveyId,
                            name: formatDateToLabel(survey.surveyDate),
                        }))}
                        selectedSurveyId={selectedSurvey.surveyId}
                        onSurveyChange={(id) => navigate({ search: { surveyId: id } })}
                    />

                    <CreatePdfButton />
                </Flex>

                {scoresByCategoryQuery.isPending && <ContentSkeleton />}

                <ErrorOverlay
                    active={scoresByCategoryQuery.isError}
                    onRetry={scoresByCategoryQuery.refetch}
                    message="カテゴリー分析の取得に失敗しました。"
                />

                {scoresByCategoryQuery.data && selectedSurvey && (
                    <SurveyCategories
                        scores={resolveScoresChain(surveysToLoad, scoresByCategoryQuery.data.scores)}
                        selectedSurvey={selectedSurvey}
                        isError={scoresByCategoryQuery.isError}
                        onRetry={scoresByCategoryQuery.refetch}
                        companyAverage={
                            overallScoresQuery.data
                                ? {
                                      di: selectedSurveyOverallScores?.di ?? null,
                                      deviation: selectedSurveyOverallScores?.deviation ?? null,
                                  }
                                : null
                        }
                        isCompanyAverageError={overallScoresQuery.isError}
                        onCompanyAverageRetry={overallScoresQuery.refetch}
                    />
                )}
            </Page.Content>
        </Page.Root>
    );
}

function SurveyCategoriesRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="結果分析 - カテゴリー" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    {/* Absolute widths: these sit in shrink-to-fit flex items, so a
                        percentage would have no definite width to resolve against. */}
                    <Flex direction="column" gap="2">
                        <Skeleton.Line width={96} height="sm" />
                        <Skeleton.Box width={300} height={42} />
                    </Flex>

                    <Skeleton.Box width={168} height={36} />
                </Flex>

                <ContentSkeleton />
            </Page.Content>
        </Page.Root>
    );
}

function SurveyCategoriesRouteError({ error, reset }: ErrorComponentProps) {
    if (error instanceof SearchParamError) {
        return <InvalidRequest kind="client" />;
    }

    if (error instanceof NotFoundError) {
        return <NotFound kind="client" />;
    }

    return (
        <RouteError
            kind="client"
            title="結果分析 - カテゴリー"
            onRetry={reset}
            message="結果分析 - カテゴリーの取得に失敗しました。"
        />
    );
}

type SurveyCategoriesProps = {
    scores: SurveyScoresByCategory[];
    selectedSurvey: Survey;
    isError: boolean;
    onRetry: () => void;
    /** Company-wide scores of the selected survey; `null` while still loading. */
    companyAverage: Pick<SurveyOverallScores, 'di' | 'deviation'> | null;
    isCompanyAverageError: boolean;
    onCompanyAverageRetry: () => void;
};

function SurveyCategories({
    scores,
    selectedSurvey,
    isError,
    onRetry,
    companyAverage,
    isCompanyAverageError,
    onCompanyAverageRetry,
}: SurveyCategoriesProps) {
    const [attributesForComparisonTable, setAttributesForComparisonTable] = useState<string[]>([]);
    const [attributesForComparisonChart, setAttributesForComparisonChart] = useState<string[]>([]);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;

    const surveyAttributesQuery = useSurveyAttributes(selectedSurveyId);

    const {
        scoresForCategoryComparisonTable,
        previousScoresForCategoryComparisonTable,
        isError: isCategoryComparisonTableError,
        retry: retryCategoryComparisonTable,
    } = useScoresForCategoriesComparisonTable(selectedSurveyId, previousSurveyId ?? null, attributesForComparisonTable);

    const {
        scoresForAttributeComparisonChart,
        isError: isAttributeComparisonChartError,
        retry: retryAttributeComparisonChart,
    } = useScoresForAttributeComparisonChart(selectedSurveyId, attributesForComparisonChart);

    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <Ranking
                    title="よかった​カテゴリー"
                    variant="positive"
                    items={currentSurveyScores.categories}
                    isError={isError}
                    onRetry={onRetry}
                />
                <Ranking
                    title="改善必要カテゴリー"
                    variant="negative"
                    items={currentSurveyScores.categories}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            <div className={pageBreakBeforeInLandscape}>
                <SurveyCategoriesComparisonChart
                    scores={scores}
                    companyAverage={companyAverage}
                    isError={isError || isCompanyAverageError}
                    onRetry={() => {
                        if (isError) onRetry();
                        if (isCompanyAverageError) onCompanyAverageRetry();
                    }}
                />
            </div>

            {/* Print layout: rankings and the year-over-year chart, then the comparisons. */}
            <div className={pageBreakBefore}>
                <SurveyCategoriesComparisonTable
                    scores={scoresForCategoryComparisonTable.data ?? null}
                    previousScores={previousScoresForCategoryComparisonTable.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={attributesForComparisonTable}
                    onSelectedAttributeOptionIdsChange={setAttributesForComparisonTable}
                    isFetching={
                        scoresForCategoryComparisonTable.isFetching ||
                        previousScoresForCategoryComparisonTable.isFetching
                    }
                    isError={isCategoryComparisonTableError}
                    onRetry={retryCategoryComparisonTable}
                />
            </div>

            <div className={pageBreakBefore}>
                <SurveyCategoryXAttributesComparisonChart
                    scores={scoresForAttributeComparisonChart.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={attributesForComparisonChart}
                    onSelectedAttributeOptionIdsChange={setAttributesForComparisonChart}
                    isFetching={scoresForAttributeComparisonChart.isFetching}
                    isError={isAttributeComparisonChartError}
                    onRetry={retryAttributeComparisonChart}
                />
            </div>
        </Flex>
    );
}

function ContentSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <RankingSkeleton />
                <RankingSkeleton />
            </Flex>

            <SurveyCategoriesComparisonChartSkeleton />

            <SurveyCategoriesComparisonTableSkeleton />

            <SurveyCategoryXAttributesComparisonChartSkeleton />
        </Flex>
    );
}
